import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/articles";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/751042991eee80e3d0f5ce87d83ca658/78871cc3cb98add911b4c909beefd628.png"
              alt="HipoBuy 2026 - Premium Fashion"
              className="h-9 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                scrolled ? "text-gray-700 hover:text-gray-900" : "text-white hover:text-gray-200"
              }`}
            >
              Home
            </Link>

            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  scrolled ? "text-gray-700 hover:text-gray-900" : "text-white hover:text-gray-200"
                }`}
              >
                Categories
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? "rotate-180" : ""}`}></i>
                </div>
              </button>
              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors cursor-pointer"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={cat.icon}></i>
                      </div>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/#articles"
              className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                scrolled ? "text-gray-700 hover:text-gray-900" : "text-white hover:text-gray-200"
              }`}
            >
              Articles
            </Link>
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://repsw2c.com/"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="px-4 py-2 text-sm font-semibold border-2 border-white text-white rounded-full hover:bg-white hover:text-gray-900 transition-all cursor-pointer whitespace-nowrap"
              style={scrolled ? { borderColor: "#1a1a1a", color: "#1a1a1a" } : {}}
            >
              HipoBuy Shipping
            </a>
            <a
              href="https://repsw2c.com/"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="px-4 py-2 text-sm font-semibold bg-amber-500 text-white rounded-full hover:bg-amber-600 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1"
            >
              Buy It Now
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`text-xl ${scrolled ? "text-gray-900" : "text-white"} ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-4 py-4 space-y-3">
            <Link to="/" className="block text-sm font-medium text-gray-700 py-2 cursor-pointer" onClick={() => setMobileMenuOpen(false)}>
              Home
            </Link>
            <div>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1 text-sm font-medium text-gray-700 py-2 cursor-pointer w-full"
              >
                Categories
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? "rotate-180" : ""}`}></i>
                </div>
              </button>
              {dropdownOpen && (
                <div className="pl-4 space-y-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="block text-sm text-gray-600 py-1.5 cursor-pointer hover:text-gray-900"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 pt-2">
              <a
                href="https://repsw2c.com/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-center px-4 py-2 text-sm font-semibold border-2 border-gray-900 text-gray-900 rounded-full cursor-pointer whitespace-nowrap"
              >
                HipoBuy Shipping
              </a>
              <a
                href="https://repsw2c.com/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-center px-4 py-2 text-sm font-semibold bg-amber-500 text-white rounded-full cursor-pointer whitespace-nowrap"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
